import { baseQuery, signRequest } from '@/shared/api/utils';
import { REQUESTS_OPTIONS } from '@/shared/utils/constants.ts';

import {
  AssetsAPIResponse,
  AssetsCreateRequest,
  AssetsTagsUpdateRequest,
  AssetsUpdateRequest,
  OrganizationBodyRequest,
  OrganizationUpdateRequest,
} from '@/shared/api/assets/assets.types';
import {
  AssetCategory,
  AssetOrganization,
  GeofenceOccupants,
} from '@/shared/types/assets/assets.types';
import {
  MinimizedTagBody,
  TagBody,
  UpdateAssetAttachedTagsRequest,
} from '@/shared/types/assets/tags.types.ts';
import { ZPSUser } from '@/shared/types/users/user.types';

export namespace AssetsAPINamespace {
  const userClient = baseQuery('USER');
  const newClient = baseQuery('NEW_API');

  export const getAssetsGeofenceOccupants = async (
    accountResName: string,
    siteResName: string,
    accessToken: string,
  ): Promise<GeofenceOccupants[]> => {
    const url = `/accounts/${accountResName}/sites/${siteResName}/assets/enclosing-geofences`;

    const body = {
      checkTime: new Date(Date.now()).setMinutes(new Date(Date.now()).getMinutes() - 3),
    };

    const result = await newClient.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return result.data.data;
  };

  export const patchCategoryPath = async (
    accountResName: string,
    siteResName: string,
    treeResName: string,
    pathResName: string,
    accessToken: string,
    body: {
      categoryPath: string;
      categoryPathStatus: string;
    },
  ): Promise<AssetCategory> => {
    const url = `/accounts/${accountResName}/sites/${siteResName}/assets/category-trees/${treeResName}/categories/${pathResName}`;

    const result = await newClient.patch(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return result.data;
  };

  export const createCategoryPath = async (
    accountResName: string,
    siteResName: string,
    treeResName: string,
    accessToken: string,
    body: {
      categoryPath: string;
      categoryPathStatus: string;
    },
  ): Promise<AssetCategory> => {
    const url = `/accounts/${accountResName}/sites/${siteResName}/assets/category-trees/${treeResName}/categories`;

    const result = await newClient.post(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return result.data;
  };

  export const getAllOrganizations = async (
    accountResName: string,
    siteResName: string,
    accessToken: string,
  ): Promise<AssetOrganization[]> => {
    const url = `/organizations/accounts/${accountResName}/sites/${siteResName}`;

    const result = await newClient.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return result.data?.data?.items ?? [];
  };

  export const getAssetAttachedTags = async (
    accountResourceName: string,
    siteResourceName: string,
    assetResourceName: string,
    accessToken: string,
  ): Promise<MinimizedTagBody[]> => {
    const url = `/accounts/${accountResourceName}/sites/${siteResourceName}/assets/${assetResourceName}/attached-tags`;

    const result = await newClient.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return result.data.data;
  };

  export const updateAssetAttachedTags = async ({
    accessToken,
    accountResourceName,
    assetResourceName,
    siteResourceName,
    ...body
  }: UpdateAssetAttachedTagsRequest): Promise<TagBody[]> => {
    const url = `/accounts/${accountResourceName}/sites/${siteResourceName}/assets/${assetResourceName}/attached-tags`;

    const { data } = await newClient.put(url, body, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return data.data;
  };

  export const createOne = async ({
    accessToken,
    accountResName,
    siteResName,
    ...body
  }: AssetsCreateRequest): Promise<AssetsAPIResponse> => {
    const url = `/accounts/${accountResName}/sites/${siteResName}/assets`;

    const result = await newClient.post(url, body, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return result.data;
  };

  export const updateOne = async ({
    accessToken,
    accountResName,
    assetResName,
    siteResName,
    ...body
  }: AssetsUpdateRequest): Promise<AssetsAPIResponse> => {
    const url = `/accounts/${accountResName}/sites/${siteResName}/assets/asset/${assetResName}`;

    const result = await newClient.patch(url, body, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return result.data;
  };

  export const attachTags = async ({
    accountResName,
    assetResName,
    siteResName,
    tags,
    accessToken,
  }: AssetsTagsUpdateRequest): Promise<TagBody[]> => {
    const url = `/accounts/${accountResName}/sites/${siteResName}/assets/${assetResName}/attached-tags`;

    const body = { tags };

    const result = await newClient.put(url, body, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return result.data.data;
  };

  export const createOneOrganization = async ({
    accessToken,
    accountResName,
    ...body
  }: OrganizationBodyRequest): Promise<string> => {
    const url = `/organizations/accounts/${accountResName}`;

    const requestBody = {
      orgName: body.orgName,
      orgStatus: body.status || 'inactive',
      settingsAdditionalProperties: body.extraProperties || [],
    };

    const result = await newClient.post(url, requestBody, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return result.data.data;
  };

  export const updateOneOrganization = async ({
    accessToken,
    accountResName,
    orgResName,
    ...body
  }: OrganizationUpdateRequest): Promise<AssetOrganization> => {
    const url = `/organizations/accounts/${accountResName}/identifiers/${orgResName}`;

    const requestBody = {
      orgName: body.orgName,
      orgStatus: body.status || 'inactive',
      settingsAdditionalProperties: body.extraProperties || [],
    };

    const result = await newClient.patch(url, requestBody, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    //New api returns { message, data, timestamp }
    return {
      extraProperties: body.extraProperties || [],
      orgName: body.orgName || '',
      orgResName: result.data.data,
      status: body.status || 'inactive',
    };
  };

  export const getZPSUsers = async (accountResName: string): Promise<ZPSUser[]> => {
    const url = `/accounts/${accountResName}/users?limit=${REQUESTS_OPTIONS.LIMIT_POINTS}`;
    const signedRequest = await signRequest('USER', url, 'GET');

    const result = await userClient.get(signedRequest.url, {
      headers: signedRequest.headers,
    });

    return result.data.results;
  };

  export const getZPSUserDetails = async (
    accountResName: string,
    userResName: string,
  ): Promise<ZPSUser> => {
    const url = `/accounts/${accountResName}/users/${userResName}`;
    const signedRequest = await signRequest('USER', url, 'GET');

    const result = await userClient.get(signedRequest.url, {
      headers: signedRequest.headers,
    });

    return result.data;
  };
}
